import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {CheckboxButton} from 'shells/button/checkbox-button/checkbox-button';
import {convertToModels} from 'shells/filter-capsule/convert-to-models';
import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';
import {SearchBar} from 'shells/search-bar/search-bar';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import type {NutshellState} from '../../../../store';

import styles from './template-groups-filter-section.module.css';

type Props = {
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject, arg2: string) => void;
    onParamChange: (arg1: any, arg2: boolean) => void;
    onlyArchived: boolean;
    justMine: boolean;
    search: string;
    count: number;
    onRemoveAllFilters: () => void;
    isFiltering: boolean;
};

export function TemplateGroupsFilterSection(props: Props) {
    const assignables = useSelector((state: NutshellState) => Assignables.getAll(state));
    const {onlyArchived, justMine, search} = props;
    const {user} = useGetSessionUser();

    const creatorFilter = safelyGetFilterByKey(props.activeFilters, 'creator');

    return (
        <>
            {user && user.permissions.canManageEmailTemplates ? (
                <FilterCapsule
                    key='creator'
                    size='big'
                    collection={convertToModels(assignables)}
                    filter={creatorFilter}
                    filterName='creator'
                    defaultValueTitle='Creator'
                    disabled={onlyArchived}
                    schemaField={{
                        name: 'creator',
                        type: 'relationship',
                        title: 'Creator',
                        description: "The Nutshell user or team that 'created' the Email template",
                        isFilterable: true,
                        isDisplayable: true,
                        isSortable: true,
                        isAndable: true,
                        relationships: ['users', 'teams'],
                        size: 'md',
                    }}
                    onSubmit={(filterObject) => {
                        // If we have a creator filter, clear justMine
                        props.onParamChange({filter: {...filterObject, justMine: false}}, false);
                    }}
                    onRemove={() => {
                        props.onUpdateFilters({creator: []}, 'creator');
                    }}
                />
            ) : null}
            <div className={styles['filter-group']}>
                <CheckboxButton
                    // Disabled if we have a creator filter
                    disabled={
                        onlyArchived ||
                        Boolean(
                            creatorFilter &&
                                creatorFilter.data &&
                                Array.isArray(creatorFilter.data) &&
                                creatorFilter.data.length
                        )
                    }
                    label='Just mine'
                    checked={justMine}
                    onChange={() => {
                        props.onUpdateFilters(
                            {
                                justMine: !justMine,
                            },
                            'justMine'
                        );
                    }}
                />
            </div>
            {onlyArchived ? undefined : (
                <div className={styles['filter-group']}>
                    <SearchBar
                        value={search}
                        // @ts-expect-error
                        isDisabled={onlyArchived}
                        placeholder='Search templates…'
                        isClearable={true}
                        hasBorder={true}
                        autoFocus={false}
                        onChange={(value) => {
                            props.onUpdateFilters(
                                {
                                    search: value,
                                },
                                'search'
                            );
                        }}
                        styleOptions={{height: '29px'}}
                        hiddenLabel='Search'
                    />
                </div>
            )}
            <div className={styles['template-count']}>{`${props.count} ${
                props.count === 1 ? 'template' : 'templates'
            }`}</div>
            {props.isFiltering ? (
                <Button size='small' variant='text-primary' onClick={props.onRemoveAllFilters}>
                    Remove filters
                </Button>
            ) : null}
        </>
    );
}
